import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { useAuth } from '@/hooks/useAuth';
import {
  COMPLIANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/compliancePermissions';
import {
  usePolicyFiles,
  type CreatePolicyFileDto,
  type PolicyFileDto,
} from '@/hooks/useCompliance';
import {
  POLICY_FILE_STATUS_LABELS,
  RISK_LEVEL_LABELS,
  RISK_LEVEL_LIST,
  formatEnumLabel,
} from '@/lib/enumLabels';

const POLICY_FILE_STATUSES: readonly PolicyFileDto['status'][] = [
  'draft',
  'active',
  'superseded',
  'expired',
  'archived',
];

const STATUS_OPTIONS: ReadonlyArray<PolicyFileDto['status'] | ''> = ['', ...POLICY_FILE_STATUSES];

const NEXT_STATUS_MAP: Readonly<Record<PolicyFileDto['status'], readonly PolicyFileDto['status'][]>> = {
  draft: ['active', 'archived'],
  active: ['superseded', 'expired', 'archived'],
  superseded: [],
  expired: ['archived'],
  archived: [],
};

function fmtDate(value?: string): string {
  return value ? new Date(value).toLocaleDateString('zh-CN') : '—';
}

function riskTone(level: PolicyFileDto['riskLevel']): string {
  if (level === 'critical') return 'text-red-700 bg-red-100';
  if (level === 'high') return 'text-orange-700 bg-orange-100';
  if (level === 'medium') return 'text-amber-700 bg-amber-100';
  return 'text-emerald-700 bg-emerald-100';
}

export default function PolicyFilesPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, COMPLIANCE_PERMISSIONS.POLICY_FILE_READ);
  const canCreate = hasPermission(permissions, COMPLIANCE_PERMISSIONS.POLICY_FILE_CREATE);
  const canUpdate = hasPermission(permissions, COMPLIANCE_PERMISSIONS.POLICY_FILE_UPDATE);
  const { policyFiles, loading, error, fetchPolicyFiles, createPolicyFile, transitionPolicyFile } = usePolicyFiles();

  const [statusFilter, setStatusFilter] = useState<PolicyFileDto['status'] | ''>('');
  const [createForm, setCreateForm] = useState<CreatePolicyFileDto>({
    title: '',
    docOssKey: '',
    effectiveFrom: '',
    riskLevel: 'medium',
    responsibleUserId: '',
    issuingAuthority: '',
    summary: '',
    supersedesId: '',
  });
  const [transitionTargetId, setTransitionTargetId] = useState('');
  const [transitionVersion, setTransitionVersion] = useState(0);
  const [transitionStatus, setTransitionStatus] = useState<PolicyFileDto['status']>('active');

  useEffect(() => {
    if (!canRead) {
      return;
    }
    void fetchPolicyFiles({ status: statusFilter || undefined });
  }, [canRead, statusFilter, fetchPolicyFiles]);

  if (!canRead) {
    return <AccessDenied resource="政策文件" />;
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const result = await createPolicyFile(createForm);
    if (result) {
      setCreateForm({
        title: '',
        docOssKey: '',
        effectiveFrom: '',
        riskLevel: 'medium',
        responsibleUserId: '',
        issuingAuthority: '',
        summary: '',
        supersedesId: '',
      });
      void fetchPolicyFiles({ status: statusFilter || undefined });
    }
  }

  async function handleTransition(item: PolicyFileDto) {
    if (!canUpdate) {
      return;
    }
    const next = transitionTargetId === item.id ? transitionStatus : NEXT_STATUS_MAP[item.status][0];
    if (!next) {
      return;
    }
    const result = await transitionPolicyFile(item.id, {
      expectedVersion: transitionTargetId === item.id ? transitionVersion : item.version,
      nextStatus: next,
    });
    if (result) {
      void fetchPolicyFiles({ status: statusFilter || undefined });
      setTransitionTargetId('');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">政策文件</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            管理对外监管文件、内部合规通知和项目授权依据。
          </p>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as PolicyFileDto['status'] | '')}
          className="rounded border border-gray-300 px-3 py-2 text-sm"
        >
          {STATUS_OPTIONS.map((item) => (
            <option key={item || 'all'} value={item}>
              {item ? formatEnumLabel(item, POLICY_FILE_STATUS_LABELS, '未知状态') : '全部状态'}
            </option>
          ))}
        </select>
      </div>

      {canCreate && (
        <form onSubmit={(e) => { void handleCreate(e); }} className="grid gap-4 rounded-lg border bg-white p-5 shadow-sm lg:grid-cols-2">
          <div className="lg:col-span-2 text-lg font-semibold">新建政策文件</div>
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="文件标题"
            value={createForm.title}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, title: e.target.value }))}
            required
          />
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="OSS 文档 Key"
            value={createForm.docOssKey}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, docOssKey: e.target.value }))}
            required
          />
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="发文机构"
            value={createForm.issuingAuthority ?? ''}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, issuingAuthority: e.target.value }))}
          />
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="责任人用户 ID"
            value={createForm.responsibleUserId}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, responsibleUserId: e.target.value }))}
            required
          />
          <input
            type="date"
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            value={createForm.effectiveFrom}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, effectiveFrom: e.target.value }))}
            required
          />
          <input
            type="date"
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            value={createForm.effectiveTo ?? ''}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, effectiveTo: e.target.value || undefined }))}
          />
          <select
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            value={createForm.riskLevel}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, riskLevel: e.target.value as CreatePolicyFileDto['riskLevel'] }))}
          >
            {RISK_LEVEL_LIST.map((level) => (
              <option key={level} value={level}>
                {RISK_LEVEL_LABELS[level]}
              </option>
            ))}
          </select>
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="被替代文件 ID（可选）"
            value={createForm.supersedesId ?? ''}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, supersedesId: e.target.value || undefined }))}
          />
          <textarea
            className="min-h-24 rounded border border-gray-300 px-3 py-2 text-sm lg:col-span-2"
            placeholder="摘要"
            value={createForm.summary ?? ''}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, summary: e.target.value }))}
          />
          <div className="lg:col-span-2 flex justify-end">
            <button
              type="submit"
              className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              创建文件
            </button>
          </div>
        </form>
      )}

      {error && <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">{error.message}</div>}

      <div className="overflow-x-auto rounded-lg border bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">标题</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">状态</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">风险</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">生效区间</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">责任人</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">版本</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">加载中…</td>
              </tr>
            ) : policyFiles.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">暂无文件</td>
              </tr>
            ) : (
              policyFiles.map((item) => {
                const nextStatuses = NEXT_STATUS_MAP[item.status];
                return (
                  <tr key={item.id} className="border-t">
                    <td className="px-4 py-3">
                      <div className="font-medium">{item.title}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{item.summary || item.docOssKey}</div>
                    </td>
                    <td className="px-4 py-3">{formatEnumLabel(item.status, POLICY_FILE_STATUS_LABELS, '未知状态')}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${riskTone(item.riskLevel)}`}>
                        {formatEnumLabel(item.riskLevel, RISK_LEVEL_LABELS, '未知')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{fmtDate(item.effectiveFrom)} - {fmtDate(item.effectiveTo)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{item.responsibleUserId}</td>
                    <td className="px-4 py-3">{item.version}</td>
                    <td className="px-4 py-3">
                      {canUpdate && nextStatuses.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <select
                            className="rounded border border-gray-300 px-2 py-1 text-xs"
                            value={transitionTargetId === item.id ? transitionStatus : nextStatuses[0]}
                            onChange={(e) => {
                              setTransitionTargetId(item.id);
                              setTransitionVersion(item.version);
                              setTransitionStatus(e.target.value as PolicyFileDto['status']);
                            }}
                          >
                            {nextStatuses.map((status) => (
                              <option key={status} value={status}>
                                {formatEnumLabel(status, POLICY_FILE_STATUS_LABELS, '未知状态')}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => { void handleTransition(item); }}
                            className="rounded bg-slate-900 px-2 py-1 text-xs text-white hover:bg-slate-700"
                          >
                            流转
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">无可用动作</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
